import { useMutation } from '@tanstack/react-query';
import { useEffect, useState, type JSX } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '@/lib/api/error';
import { deallocateMachineRequest } from '@/services/machines';
import type { DeallocateMachineBody } from '@/types/machines';

/**
 * Plan 11-02 — DeallocateMachineDialog
 *
 * Closes the open allocation row for a machine. Shows the current site name
 * read-only, takes a date (defaults today) and an optional reason (max 500).
 *
 * Submit calls `deallocateMachineRequest(machineId, body)` — 204 closes the
 * dialog and fires `onSuccess` (parent invalidates queries).
 */

export interface DeallocateMachineDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly machineId: number;
  readonly currentSiteName: string;
  readonly onSuccess: () => void;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function DeallocateMachineDialog({
  open,
  onOpenChange,
  machineId,
  currentSiteName,
  onSuccess,
}: DeallocateMachineDialogProps): JSX.Element {
  const [deallocatedAt, setDeallocatedAt] = useState<string>(todayIso());
  const [reason, setReason] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDeallocatedAt(todayIso());
      setReason('');
      setErrorMessage(null);
    }
  }, [open]);

  const deallocateMutation = useMutation<undefined, ApiError, { body: DeallocateMachineBody }>({
    mutationFn: async ({ body }) => {
      await deallocateMachineRequest(machineId, body);
      return undefined;
    },
    onSuccess: () => {
      onSuccess();
      onOpenChange(false);
    },
    onError: (err) => {
      setErrorMessage(err.detail ?? err.message);
    },
  });

  function handleSubmit(): void {
    if (deallocatedAt === '' || Number.isNaN(Date.parse(deallocatedAt))) {
      setErrorMessage('Pick a valid deallocation date.');
      return;
    }
    if (reason.length > 500) {
      setErrorMessage('Reason must be 500 characters or fewer.');
      return;
    }
    setErrorMessage(null);
    const body: DeallocateMachineBody = {
      deallocatedAt: new Date(`${deallocatedAt}T00:00:00Z`).toISOString(),
      reason: reason.trim() === '' ? null : reason.trim(),
    };
    deallocateMutation.mutate({ body });
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="deallocate-machine-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Deallocate from {currentSiteName}?</AlertDialogTitle>
          <AlertDialogDescription>
            The current open allocation row will close. The machine will become unallocated.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="dmd-deallocated-at">Deallocation date</Label>
            <Input
              id="dmd-deallocated-at"
              type="date"
              value={deallocatedAt}
              onChange={(e) => {
                setDeallocatedAt(e.target.value);
              }}
              disabled={deallocateMutation.isPending}
              data-testid="dmd-deallocated-at"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="dmd-reason">Reason (optional, max 500 chars)</Label>
            <Textarea
              id="dmd-reason"
              rows={3}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              disabled={deallocateMutation.isPending}
              maxLength={500}
              data-testid="dmd-reason"
            />
          </div>
        </div>

        {errorMessage != null && errorMessage !== '' && (
          <Alert variant="destructive" data-testid="dmd-error">
            <AlertTitle>Could not deallocate</AlertTitle>
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        <AlertDialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={deallocateMutation.isPending}
            data-testid="dmd-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleSubmit}
            disabled={deallocateMutation.isPending}
            data-testid="dmd-submit"
          >
            {deallocateMutation.isPending ? 'Deallocating…' : 'Deallocate'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
